import { defineStore, storeToRefs } from 'pinia';
import { fetchWrapper } from '@/helpers';
import { useAccountStore, useAlertStore } from '.';
import { updateLicenses } from './account.store';

const baseUrl = `${process.env.VUE_APP_VUE_API_URL}`;
export const useProxyStore = defineStore('proxy', {
    state: () => ({
        proxyList: [],
        loading: false,
    }),
    actions: {
        async refreshDisplayStatus() {
            const live = (p) => p.status === 'ACTIVE' && (p.m_type === 'PROXYSMART' || p.m_type === 'X_PROXY' || p.m_type === 'PROPROXYUI');
            if (!this.proxyList.some(live)) return;
            try {
                const { statuses } = await fetchWrapper.post(`${baseUrl}/proxy/display-status`);
                const byId = new Map(statuses.map(s => [s.uuid, s]));
                this.proxyList = this.proxyList.map(p => {
                    const status = byId.get(p.uuid);
                    return status ? { ...p, ...status } : p;
                });
            } catch (_) {
                // Do not leave a stale yellow/green observation on a disconnected page.
                this.proxyList = this.proxyList.map(p => live(p)
                    ? { ...p, display_status: 'checking', status_server_online: undefined } : p);
            }
        },
        async getProxyList() {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.post(`${baseUrl}/proxy/my-list`);
                const previous = new Map(this.proxyList.map(p => [p.uuid, p]));
                this.proxyList = res.proxyList.map(p => {
                    if (p.m_type !== 'PROXYSMART' || p.status !== 'ACTIVE') return p;
                    const observed = previous.get(p.uuid);
                    return { ...p, display_status: observed?.display_status || 'checking',
                        status_checked_at: observed?.status_checked_at,
                        status_server_online: observed?.status_server_online };
                });
                await this.refreshDisplayStatus();
                updateLicenses()
            }
            catch (error) {
                alertStore.error(error);
            }
            return
        },
        async updateAutoRotationTime(uuid, minute) {
            const alertStore = useAlertStore()
            try {
                await fetchWrapper.post(`${baseUrl}/license/change-rotation-time`, { uuid, minute: minute === 0 ? -1 : minute });
                alertStore.success("You set successfully")
                return
            } catch (error) {
                alertStore.error(error)
            }
            return

        },
        async updateAutoExtend(uuid, extend_flag) {
            const alertStore = useAlertStore()
            try {
                await fetchWrapper.post(`${baseUrl}/license/update-auto-extend`, { uuid, auto_extend: extend_flag });
                return true
            } catch (error) {
                alertStore.error(error)
            }
            return false

        },
        async updateLicense(uuid) {
            const alertStore = useAlertStore()
            try {
                await fetchWrapper.post(`${baseUrl}/license/update-license`, { uuid });
                await this.getProxyList()
                alertStore.success("Generated a new rotation URL. The previous URL no longer works.")
            } catch (error) {
                alertStore.error(error)
            }
            return false

        },
        async rebootDevice(license) {
            const alertStore = useAlertStore()
            try {
                this.loading = true;
                const res = await fetchWrapper.post(`${baseUrl}/proxy/reboot-device`, { license });
                this.loading = false;
                alertStore.success(res.message)
            } catch (error) {
                this.loading = false;
                alertStore.error(error)
            }
            return

        },
        async updateUserAuthentication(license, username, ipAddress) {
            const alertStore = useAlertStore()
            try {
                const res = await fetchWrapper.post(`${baseUrl}/proxy/update-user-authentication`, { license, username, ipAddress });
                // this.proxyList = this.proxyList.map(proxy=>{
                //     if(proxy.uuid===license){
                //         return res.proxy
                //     }
                //     else{
                //         return proxy
                //     }
                // });
                updateLicenses()
                alertStore.success("Updated Auth")

            } catch (error) {
                alertStore.error(error)
            }
            return
        },
        async updateNote(license, note) {
            const alertStore = useAlertStore()
            try {
                await fetchWrapper.post(`${baseUrl}/proxy/update-note`, { license, note });
                updateLicenses()
                alertStore.success("Updated Note")

            } catch (error) {
                alertStore.error(error)
            }
            return
        },
        async updateCoupon(license, couponCode) {
            const alertStore = useAlertStore()
            try {
                await fetchWrapper.post(`${baseUrl}/proxy/update-coupon`, { license, couponCode });
                updateLicenses()
                alertStore.success("Updated Note")

            } catch (error) {
                alertStore.error(error)
            }
            return
        },
        // ---- per-proxy VPN (XPROXY_NEW) ----
        async getVpn(uuid) {
            try { return await fetchWrapper.get(`${baseUrl}/proxy/vpn?license=${encodeURIComponent(uuid)}`); }
            catch (error) { useAlertStore().error(error); return null }
        },
        // background refresh of the VPN state: no error pop-up if a server is slow
        async getVpnQuiet(uuid) {
            try { return await fetchWrapper.get(`${baseUrl}/proxy/vpn?license=${encodeURIComponent(uuid)}`); }
            catch (error) { return null }
        },
        async enableVpn(uuid, type) {
            try { return await fetchWrapper.post(`${baseUrl}/proxy/vpn/enable`, { uuid, type }); }
            catch (error) { useAlertStore().error(error); return null }
        },
        async disableVpn(uuid) {
            try { return await fetchWrapper.post(`${baseUrl}/proxy/vpn/disable`, { uuid }); }
            catch (error) { useAlertStore().error(error); return null }
        },
        async vpnQrLink(uuid) {   // ProProxyUI: a 10-minute .ovpn link for the OpenVPN QR code
            try { return await fetchWrapper.post(`${baseUrl}/proxy/vpn/qr-link`, { uuid }); }
            catch (error) { useAlertStore().error(error); return null }
        },
        async vpnFile(uuid, type) {   // type: ProProxyUI only, 'wg' or 'ovpn'
            try { return await fetchWrapper.get(`${baseUrl}/proxy/vpn/file?license=${encodeURIComponent(uuid)}${type ? '&type=' + encodeURIComponent(type) : ''}`); }
            catch (error) { useAlertStore().error(error); return null }
        },
        async getOsOptions(uuid, fresh) {
            try {
                if (!this._osOptionsCache) this._osOptionsCache = {}
                if (this._osOptionsCache[uuid] && !fresh) return this._osOptionsCache[uuid]
                const res = await fetchWrapper.get(`${baseUrl}/proxy/os-options?license=${encodeURIComponent(uuid)}`);
                if (res && res.options) this._osOptionsCache[uuid] = res
                return res;
            } catch (error) {
                useAlertStore().error(error)
                return null
            }
        },
        async changeOS(uuid, osType, mtu) {
            const alertStore = useAlertStore()
            try {
                await fetchWrapper.post(`${baseUrl}/proxy/change-os`, { uuid, osType, ...(mtu !== undefined ? { mtu } : {}) });
                if (this._osOptionsCache) delete this._osOptionsCache[uuid]   // the window shows the new choice next time
                alertStore.success("Updated OS Spoofing")

            } catch (error) {
                alertStore.error(error)
            }
            return
        },
        async quoteExtension(license, time_unit, couponCode) {
            const alertStore = useAlertStore()
            try {
                return await fetchWrapper.post(`${baseUrl}/license/extension-quote`, { license, time_unit, couponCode });
            } catch (error) {
                alertStore.error(error)
                return null
            }
        },
        async extendLicense(license, time_unit, couponCode = '', expectedPrice = null) {
            const alertStore = useAlertStore()
            try {
                const request = { license, time_unit, couponCode };
                if (expectedPrice !== null) request.expectedPrice = expectedPrice;
                const res = await fetchWrapper.post(`${baseUrl}/license/extend`, request);
                // this.proxyList = this.proxyList.map(proxy=>{
                //     if(proxy.uuid===license){
                //         return res.proxy
                //     }
                //     else{
                //         return proxy
                //     }
                // });
                const accountStore = useAccountStore()
                const account = storeToRefs(accountStore)
                account.user.value.balance = res.balance
                updateLicenses()
                alertStore.success("Extend Success")
                return true
            } catch (error) {
                alertStore.error(error)
                return false
            }

        },
        async changeIp(license) {
            const alertStore = useAlertStore()
            try {
                await fetchWrapper.post(`${baseUrl}/proxy/change-ip-authenticated`, { license });
                return
            } catch (error) {
                alertStore.error(error)
            }
            return

        },
        async refreshIp(license) {
            const alertStore = useAlertStore()
            try {
                const res = await fetchWrapper.get(`${baseUrl}/proxy/refresh-ip?license=${license}`);
                return res
            } catch (error) {
                alertStore.error(error)
            }
            return

        },
        async loadIpChangeHistory(license_uuid, data = {}) {
            const alertStore = useAlertStore()
            try {
                const res = await fetchWrapper.post(`${baseUrl}/proxy/ip-change-history`, {
                    license_uuid,
                    from: data.from || 0,
                    count: data.count || 50,
                });
                return res
            } catch (error) {
                alertStore.error(error)
                return { histories: [], total: 0 }
            }
        }
        // Other actions...
    },
});
